import { useLayoutEffect, useRef, useState } from "react";

import "./PathBackground01.css";

const DEFAULT_PATH_D = "M 0 8 C 24 8 34 22 46 56 C 56 82 72 90 100 90";
const DEFAULT_MARKER_T = [0.0, 0.0, 0.0, 0.0, 0.0];

function makeEmptyMarkers(markerCount) {
  return Array.from({ length: markerCount }, () => ({
    x: 0,
    y: 0,
    active: false,
  }));
}

function joinClasses(...parts) {
  return parts.filter(Boolean).join(" ");
}

function PathBackground01({
  trackRef,
  markerCount = 4,
  pathD = DEFAULT_PATH_D,
  markerTs = DEFAULT_MARKER_T,
  trackMode = "section-progress",
  className = "",
  svgClassName = "",
  pathClassName = "",
  markerClassName = "",
  markerActiveClassName = "",
  bulletClassName = "",
}) {
  const fallbackTrackRef = useRef(null);
  const pathRef = useRef(null);
  const rafRef = useRef(0);
  const lastTimelineRef = useRef({
    p: -1,
    x: Number.NaN,
    y: Number.NaN,
    len: -1,
    markerSig: "",
  });

  const [pathVisual, setPathVisual] = useState({
    bullet: { x: 0, y: 0 },
    markers: makeEmptyMarkers(markerCount),
  });

  useLayoutEffect(() => {
    setPathVisual({
      bullet: { x: 0, y: 0 },
      markers: makeEmptyMarkers(markerCount),
    });
  }, [markerCount]);

  useLayoutEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const applyPathSample = (p) => {
      const pathEl = pathRef.current;
      if (!pathEl) return null;
      const len = pathEl.getTotalLength();
      if (len <= 0) return null;
      const clamped = Math.min(1, Math.max(0, p));
      const pt = pathEl.getPointAtLength(clamped * len);
      return { p: clamped, x: pt.x, y: pt.y, len };
    };

    const buildMarkers = (pathEl, len, p) => {
      return Array.from({ length: markerCount }, (_, i) => {
        const raw = markerTs[i] ?? i / Math.max(1, markerCount - 1);
        const rt = Math.min(1, Math.max(0, raw));
        const pt = pathEl.getPointAtLength(rt * len);
        return {
          x: pt.x,
          y: pt.y,
          active: p >= rt,
        };
      });
    };

    const markerSignature = (markers) =>
      markers
        .map((m) => `${m.active ? 1 : 0},${m.x.toFixed(2)},${m.y.toFixed(2)}`)
        .join("|");

    const computeProgress = (trackRect) => {
      if (trackMode !== "section-progress") return 1;
      const cy = window.innerHeight / 2;
      if (trackRect.bottom < cy) return 1;
      if (trackRect.top > cy) return 0;
      const denom = trackRect.bottom - trackRect.top;
      if (denom <= 0) return 0;
      return (cy - trackRect.top) / denom;
    };

    if (reduceMotion) {
      let raf1 = 0;
      let raf2 = 0;
      let canceled = false;
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          if (canceled) return;
          const sample = applyPathSample(1);
          const pathEl = pathRef.current;
          if (!sample) return;

          if (pathEl) {
            const markers = buildMarkers(pathEl, sample.len, 1);
            lastTimelineRef.current = {
              p: sample.p,
              x: sample.x,
              y: sample.y,
              len: sample.len,
              markerSig: markerSignature(markers),
            };
            setPathVisual({
              bullet: { x: sample.x, y: sample.y },
              markers,
            });
            return;
          }

          setPathVisual({
            bullet: { x: sample.x, y: sample.y },
            markers: makeEmptyMarkers(markerCount),
          });
        });
      });

      return () => {
        canceled = true;
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }

    const compute = () => {
      const activeTrackRef = trackRef?.current ? trackRef : fallbackTrackRef;
      const trackEl = activeTrackRef.current;
      if (!trackEl) return;

      const trackRect = trackEl.getBoundingClientRect();
      if (trackRect.height <= 0) return;

      const p = Math.min(1, Math.max(0, computeProgress(trackRect)));
      const sample = applyPathSample(p);
      if (!sample) return;

      const pathEl = pathRef.current;
      if (!pathEl) return;

      const markers = buildMarkers(pathEl, sample.len, p);
      const markerSig = markerSignature(markers);

      const prev = lastTimelineRef.current;
      if (
        Math.abs(prev.p - sample.p) < 0.001 &&
        Math.abs(prev.x - sample.x) < 0.08 &&
        Math.abs(prev.y - sample.y) < 0.08 &&
        Math.abs(prev.len - sample.len) < 0.5 &&
        prev.markerSig === markerSig
      ) {
        return;
      }

      lastTimelineRef.current = {
        p: sample.p,
        x: sample.x,
        y: sample.y,
        len: sample.len,
        markerSig,
      };
      setPathVisual({
        bullet: { x: sample.x, y: sample.y },
        markers,
      });
    };

    const schedule = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        compute();
      });
    };

    lastTimelineRef.current = {
      p: -1,
      x: Number.NaN,
      y: Number.NaN,
      len: -1,
      markerSig: "",
    };

    compute();
    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);

    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [markerCount, markerTs, pathD, trackMode, trackRef]);

  const { bullet, markers } = pathVisual;

  return (
    <div
      ref={fallbackTrackRef}
      aria-hidden
      className={joinClasses("PathBackground01", className)}
    >
      <svg
        className={joinClasses("PathBackground01-svg", svgClassName)}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <path
          ref={pathRef}
          className={joinClasses("PathBackground01-track", pathClassName)}
          d={pathD}
          fill="none"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {markers.map((m, i) => (
        <div
          key={`marker-${i}`}
          className={joinClasses(
            "PathBackground01-marker",
            markerClassName,
            m.active
              ? joinClasses(
                  "PathBackground01-marker--active",
                  markerActiveClassName
                )
              : ""
          )}
          style={{
            left: `${m.x}%`,
            top: `${m.y}%`,
          }}
        />
      ))}

      <div
        className={joinClasses("PathBackground01-bullet", bulletClassName)}
        style={{
          left: `${bullet.x}%`,
          top: `${bullet.y}%`,
        }}
      />
    </div>
  );
}

export default PathBackground01;
